(() => {
  'use strict';

  if (window.BaringCart) return;

  const selectors = {
    addForm: 'form[data-baring-cart-add]',
    count: '[data-baring-cart-count]',
    dialog: '[data-baring-cart-dialog]',
    inlineError: '[data-baring-add-error]',
    lineForm: 'form[data-baring-cart-line-form]',
    metaForm: 'form[data-baring-cart-meta-form]',
    open: '[data-baring-cart-open]',
    remove: '[data-baring-cart-remove]'
  };

  const state = {
    returnFocus: null
  };

  const routeRoot = (() => {
    const configuredRoot = window.Shopify?.routes?.root || '/';
    return configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
  })();

  const routes = {
    add: `${routeRoot}cart/add.js`,
    change: `${routeRoot}cart/change.js`,
    update: `${routeRoot}cart/update.js`
  };

  function getDialog() {
    return document.querySelector(selectors.dialog);
  }

  function getSectionId(dialog = getDialog()) {
    return dialog?.dataset.sectionId || '';
  }

  function getSection(dialog = getDialog()) {
    const sectionId = getSectionId(dialog);
    return sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
  }

  function canEnhance() {
    const dialog = getDialog();
    return Boolean(
      dialog &&
      typeof dialog.showModal === 'function' &&
      typeof window.fetch === 'function' &&
      typeof window.FormData === 'function'
    );
  }

  function sectionsUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}`;
  }

  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers
      }
    });

    const responseText = await response.text();
    let payload = {};

    if (responseText) {
      try {
        payload = JSON.parse(responseText);
      } catch (error) {
        if (!response.ok) throw new Error(`Cart request failed (${response.status}).`);
        throw new Error('The cart returned an unreadable response.');
      }
    }

    if (!response.ok) {
      const message =
        (typeof payload.description === 'string' && payload.description) ||
        (typeof payload.message === 'string' && payload.message) ||
        `Cart request failed (${response.status}).`;
      const requestError = new Error(message);
      requestError.status = response.status;
      throw requestError;
    }

    return payload;
  }

  async function fetchRenderedSection(sectionId) {
    const currentUrl = new URL(window.location.href);
    const url = new URL(`${routeRoot}cart`, window.location.origin);

    // Preserve Shopify's preview context while requesting the section directly.
    // Bundled section rendering can return `null` for a static layout section;
    // `section_id` reliably renders this drawer by its section type instead.
    ['key', 'oseid', 'preview_theme_id'].forEach((parameter) => {
      const value = currentUrl.searchParams.get(parameter);
      if (value) url.searchParams.set(parameter, value);
    });
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    const sectionHtml = await response.text();

    if (!response.ok) {
      throw new Error(`The cart drawer could not be refreshed (${response.status}).`);
    }

    return sectionHtml.trim() ? sectionHtml : null;
  }

  function setDrawerBusy(isBusy) {
    const dialog = getDialog();
    if (!dialog) return;

    dialog.setAttribute('aria-busy', String(isBusy));
    dialog
      .querySelectorAll('button:not([data-baring-cart-close]), input, textarea, select')
      .forEach((control) => {
        if (isBusy && !control.disabled) {
          control.dataset.baringRequestDisabled = 'true';
          control.disabled = true;
        } else if (!isBusy && control.dataset.baringRequestDisabled === 'true') {
          control.disabled = false;
          delete control.dataset.baringRequestDisabled;
        }
      });
  }

  function setFormBusy(form, submitter, isBusy) {
    form.setAttribute('aria-busy', String(isBusy));

    if (!submitter) return;
    if (isBusy && !submitter.disabled) {
      submitter.dataset.baringRequestDisabled = 'true';
      submitter.disabled = true;
    } else if (!isBusy && submitter.dataset.baringRequestDisabled === 'true') {
      submitter.disabled = false;
      delete submitter.dataset.baringRequestDisabled;
    }
  }

  function setStatus(message = '') {
    const status = getDialog()?.querySelector('[data-baring-cart-status]');
    if (status) status.textContent = message;
  }

  function clearError(context = document) {
    const drawerError = getDialog()?.querySelector('[data-baring-cart-error]');
    if (drawerError) {
      drawerError.hidden = true;
      drawerError.textContent = '';
    }

    const inlineError = context?.querySelector?.(selectors.inlineError);
    if (inlineError) {
      inlineError.hidden = true;
      inlineError.textContent = '';
    }
  }

  function showError(error, context = null) {
    const message = error instanceof Error ? error.message : String(error);
    const inlineError = context?.querySelector?.(selectors.inlineError);

    if (inlineError) {
      inlineError.textContent = message;
      inlineError.hidden = false;
      return;
    }

    const drawerError = getDialog()?.querySelector('[data-baring-cart-error]');
    if (drawerError) {
      drawerError.textContent = message;
      drawerError.hidden = false;
      return;
    }

    document.dispatchEvent(
      new CustomEvent('baring:cart-error', {
        detail: { message },
        bubbles: true
      })
    );
  }

  function updateExternalCounts(dialog = getDialog()) {
    const value = dialog?.dataset.cartCountValue;
    if (value == null) return;

    document.querySelectorAll(selectors.count).forEach((count) => {
      count.textContent = value;
      count.dataset.cartCountValue = value;
    });
  }

  function focusLine(lineKey) {
    const dialog = getDialog();
    if (!dialog || !lineKey) return false;

    const line = [...dialog.querySelectorAll('[data-baring-cart-line]')].find(
      (candidate) => candidate.dataset.lineKey === lineKey
    );
    const target = line?.querySelector('input[name="quantity"], button, a[href]');
    if (!target) return false;
    target.focus({ preventScroll: true });
    return true;
  }

  function openDrawer(opener = null, options = {}) {
    const dialog = getDialog();
    if (!dialog || typeof dialog.showModal !== 'function') return false;

    const { rememberFocus = true, focusLineKey = '' } = options;

    if (rememberFocus && opener instanceof HTMLElement) {
      state.returnFocus = opener;
      opener.setAttribute('aria-controls', dialog.id);
    }

    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('baring-cart-is-open');

    if (focusLineKey) {
      requestAnimationFrame(() => focusLine(focusLineKey));
    }

    return true;
  }

  function closeDrawer(dialog = getDialog()) {
    if (dialog?.open) dialog.close();
  }

  async function renderCart(payload, options = {}) {
    const currentDialog = getDialog();
    const sectionId = getSectionId(currentDialog);
    const currentSection = getSection(currentDialog);

    if (!sectionId || !currentSection) {
      throw new Error('The cart drawer section is not mounted on this page.');
    }

    const wasOpen = Boolean(currentDialog.open);
    let sectionHtml = payload?.sections?.[sectionId];

    if (!sectionHtml) sectionHtml = await fetchRenderedSection(sectionId);
    if (!sectionHtml) throw new Error('The cart updated, but its drawer could not be refreshed.');

    const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextSection = parsedDocument.getElementById(`shopify-section-${sectionId}`);

    if (!nextSection) throw new Error('The cart drawer response did not contain the expected section.');

    currentSection.replaceWith(document.importNode(nextSection, true));
    updateExternalCounts();

    if (wasOpen || options.openAfter) {
      openDrawer(options.opener || null, {
        rememberFocus: Boolean(options.opener),
        focusLineKey: options.focusLineKey || ''
      });
    }

    if (options.status) setStatus(options.status);

    document.dispatchEvent(
      new CustomEvent('baring:cart-updated', {
        bubbles: true,
        detail: { response: payload, sectionId }
      })
    );
  }

  async function refreshCart(options = {}) {
    const dialog = getDialog();
    const sectionId = getSectionId(dialog);
    if (!sectionId) throw new Error('The cart drawer section is not mounted on this page.');

    const sectionHtml = await fetchRenderedSection(sectionId);
    await renderCart(
      { sections: { [sectionId]: sectionHtml } },
      options
    );
  }

  async function addFromForm(form, submitter) {
    const dialog = getDialog();
    const sectionId = getSectionId(dialog);
    const formData = new FormData(form);
    const successMessage = form.dataset.successMessage || 'Added to your bag.';
    const cartOpener =
      form.baringCartOpener instanceof HTMLElement ? form.baringCartOpener : submitter || form;

    formData.set('sections', sectionId);
    formData.set('sections_url', sectionsUrl());
    clearError(form);
    setFormBusy(form, submitter, true);
    if (dialog?.open) setDrawerBusy(true);

    try {
      const payload = await requestJson(routes.add, {
        method: 'POST',
        body: formData
      });
      await renderCart(payload, {
        openAfter: true,
        opener: cartOpener,
        status: successMessage
      });
    } catch (error) {
      showError(error, form);
    } finally {
      if (form.isConnected) setFormBusy(form, submitter, false);
      setDrawerBusy(false);
    }
  }

  async function changeLine({ lineKey, quantity, successMessage, focusLineKey = '' }) {
    const dialog = getDialog();
    const sectionId = getSectionId(dialog);
    clearError(dialog);
    setDrawerBusy(true);

    try {
      const payload = await requestJson(routes.change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity,
          sections: [sectionId],
          sections_url: sectionsUrl()
        })
      });
      await renderCart(payload, {
        status: successMessage,
        focusLineKey: quantity > 0 ? focusLineKey : ''
      });
    } catch (error) {
      showError(error, dialog);
    } finally {
      setDrawerBusy(false);
    }
  }

  async function updateCartMeta(form, submitter) {
    const dialog = getDialog();
    const sectionId = getSectionId(dialog);
    const requestBody = {
      sections: [sectionId],
      sections_url: sectionsUrl()
    };
    const note = form.querySelector('textarea[name="note"]');
    const freeGiftWrap = form.querySelector('input[type="checkbox"][name^="attributes["]');
    const attributeKey = form.dataset.attributeKey;

    if (note) requestBody.note = note.value;
    if (freeGiftWrap && attributeKey) {
      requestBody.attributes = {
        [attributeKey]: freeGiftWrap.checked ? 'Yes' : 'No'
      };
    }

    clearError(dialog);
    setDrawerBusy(true);

    try {
      const payload = await requestJson(routes.update, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      await renderCart(payload, {
        status: form.dataset.successMessage || 'Order preferences saved.'
      });
    } catch (error) {
      showError(error, dialog);
    } finally {
      if (form.isConnected) setFormBusy(form, submitter, false);
      setDrawerBusy(false);
    }
  }

  document.addEventListener('click', async (event) => {
    const openTrigger = event.target.closest(selectors.open);
    if (openTrigger) {
      if (!canEnhance()) return;
      event.preventDefault();
      clearError();
      openDrawer(openTrigger);
      setStatus('Updating your bag.');
      setDrawerBusy(true);

      try {
        await refreshCart({ status: '' });
      } catch (error) {
        showError(error, getDialog());
      } finally {
        setDrawerBusy(false);
      }
      return;
    }

    const closeTrigger = event.target.closest('[data-baring-cart-close]');
    if (closeTrigger) {
      const dialog = closeTrigger.closest(selectors.dialog);
      if (!dialog) return;
      event.preventDefault();
      closeDrawer(dialog);
      return;
    }

    const removeTrigger = event.target.closest(selectors.remove);
    if (removeTrigger) {
      if (!canEnhance()) return;
      event.preventDefault();
      await changeLine({
        lineKey: removeTrigger.dataset.lineKey,
        quantity: 0,
        successMessage: removeTrigger.dataset.successMessage || 'Item removed.'
      });
      return;
    }

    const dialogBackdrop = event.target.matches?.(selectors.dialog) ? event.target : null;
    if (dialogBackdrop) closeDrawer(dialogBackdrop);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !canEnhance()) return;

    if (form.matches(selectors.addForm)) {
      event.preventDefault();
      await addFromForm(form, event.submitter);
      return;
    }

    if (form.matches(selectors.lineForm)) {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const lineKey = form.elements.id?.value;
      const quantity = Number(form.elements.quantity?.value);
      if (!lineKey || !Number.isInteger(quantity) || quantity < 0) {
        showError(new Error('Enter a valid whole-number quantity.'), form.closest(selectors.dialog));
        return;
      }

      await changeLine({
        lineKey,
        quantity,
        successMessage: form.dataset.successMessage || 'Quantity updated.',
        focusLineKey: lineKey
      });
      return;
    }

    if (form.matches(selectors.metaForm)) {
      event.preventDefault();
      await updateCartMeta(form, event.submitter);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches?.(selectors.dialog)) return;
      document.documentElement.classList.remove('baring-cart-is-open');
      const returnFocus = state.returnFocus;
      state.returnFocus = null;
      if (returnFocus?.isConnected) requestAnimationFrame(() => returnFocus.focus());
    },
    true
  );

  document.addEventListener('baring:cart-open', (event) => {
    if (!canEnhance()) return;
    openDrawer(event.detail?.opener || document.activeElement);
  });

  updateExternalCounts();

  window.BaringCart = Object.freeze({
    close: closeDrawer,
    open: openDrawer,
    refresh: refreshCart
  });
})();
